import React, { useMemo } from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { TEXTS } from '../../utils/constants';
import { parseEpochToDateString } from '../../utils/helpers';
import { getBoardAssociateCounts } from './utils';

/**
 * A thin, static status bar for the VLAB Labor Board.
 *
 * Displays at-a-glance associate counts for the currently selected board
 * (one cycle + phase + period). All values are derived from shiftAllocationModel,
 * so the bar updates automatically on drag/drop, Remove All, Re-run, and Save.
 *
 * Also shows plan-level FCLM tracking info (last tracked at / by), read from the
 * cached plan response. This is the same value across every cycle/period board.
 *
 * Note: "Clocked In" and "Unassigned" rely on enrichment data (clockedInStatus),
 * which is polled separately; both may read 0 until the first enrichment poll returns.
 */
const StatusItem = ({ label, value }) => (
  <Column spacing="none" alignmentHorizontal="center" spacingInset="none">
    <Text type={TEXTS.H2} alignment="center">
      {value}
    </Text>
    <Text type={TEXTS.T3} color="secondary" alignment="center">
      {label}
    </Text>
  </Column>
);

// Strip the @amazon.com suffix for a cleaner display login.
const formatTrackedBy = (trackedBy) => {
  if (!trackedBy) return '';
  return trackedBy.replace(/@amazon\.com$/i, '');
};

const FclmStatusItem = ({ cachedResponse }) => {
  const { laborTracking } = cachedResponse || {};
  const { laborTrackedAt, laborTrackedBy } = laborTracking || {};

  const detail = useMemo(() => {
    if (laborTrackedAt) {
      const when = parseEpochToDateString(laborTrackedAt);
      const who = formatTrackedBy(laborTrackedBy);
      return who ? `${when} by ${who}` : when;
    }
    return 'Not tracked yet';
  }, [laborTrackedAt, laborTrackedBy]);

  return (
    <Column spacing="none" alignmentHorizontal="center" spacingInset="none">
      <Text type={TEXTS.T2} alignment="center">
        {detail}
      </Text>
      <Text type={TEXTS.T3} color="secondary" alignment="center">
        Last FCLM Track
      </Text>
    </Column>
  );
};

const BoardStatusBar = ({ shiftAllocationModel, cachedResponse }) => {
  const { rostered, clockedIn, assigned, unassigned } = useMemo(
    () => getBoardAssociateCounts(shiftAllocationModel),
    [shiftAllocationModel]
  );

  return (
    <Box type="fill" spacingInset="300" width="100%">
      <Row alignmentHorizontal="center" alignmentVertical="center" spacing="600" wrap="down">
        <StatusItem label="Rostered" value={rostered} />
        <StatusItem label="Clocked In" value={clockedIn} />
        <StatusItem label="Assigned" value={assigned} />
        <StatusItem label="Unassigned" value={unassigned} />
        <FclmStatusItem cachedResponse={cachedResponse} />
      </Row>
    </Box>
  );
};

export default BoardStatusBar;
